import React, { useEffect } from 'react';
import { X, CornerDownLeft } from 'lucide-react';

/**
 * EmbedDrillBreadcrumb — the small chip shown at the top of an embed
 * surface after the host confirms a drill-through with `drill.response`.
 *
 * The host posts `{source:'inzara.host', type:'drill.response', target,
 * label, ttl_ms?}` after opening the drill target in its own UI (e.g. a
 * CRM modal, a routed page). The embed page renders this chip so the
 * viewer has a clear return path even though the drill happens OUTSIDE
 * the iframe — that's the touch that makes host + INZARA feel like one
 * product, not portal-plus-widget.
 *
 * The chip auto-dismisses after `ttl_ms` (if provided) and emits
 * `drill.dismissed` when the user clicks the X.
 */
export default function EmbedDrillBreadcrumb({ crumb, onDismiss, primaryColor }) {
  useEffect(() => {
    if (!crumb || !crumb.ttl_ms) return undefined;
    const t = setTimeout(() => onDismiss('timeout'), crumb.ttl_ms);
    return () => clearTimeout(t);
  }, [crumb, onDismiss]);

  if (!crumb) return null;

  return (
    <div
      className="mb-3 flex items-center justify-between gap-2 rounded-full border px-3 py-1.5 text-xs"
      style={
        primaryColor
          ? { borderColor: primaryColor, backgroundColor: `${primaryColor}18`, color: primaryColor }
          : { borderColor: 'rgb(148 163 184 / 0.4)', backgroundColor: 'rgb(148 163 184 / 0.10)' }
      }
      data-testid="embed-drill-breadcrumb"
    >
      <div className="flex items-center gap-2 min-w-0">
        <CornerDownLeft className="w-3 h-3 shrink-0" />
        <span className="truncate">{crumb.label}</span>
      </div>
      <button
        type="button"
        onClick={() => onDismiss('user')}
        className="opacity-70 hover:opacity-100 shrink-0"
        aria-label="Dismiss breadcrumb"
        data-testid="embed-drill-breadcrumb-dismiss"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
